import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { refreshCmsFeed } from '../cms/store'
import {
  DEFAULT_AUTHOR,
  MAX_UPLOAD_BYTES,
  POST_TYPES,
  defaultScheduleLocal,
  getPostType,
  postTypeForCategory,
  toDatetimeLocal,
  type PostStatus,
} from '../lib/cms'
import { mediaUrl } from '../lib/media'
import { api, apiUpload, type AdminPost } from './api'
import { useAdminAuthors } from './authorsStore'
import FilePicker from './FilePicker'
import PostEditor from './PostEditor'
import { useIsAdmin } from './role'

const inputClass = 'w-full border border-rule bg-paper px-3 py-2.5 text-[15px] outline-none focus:border-ink'
const labelClass = 'text-caps mb-1.5 block text-[10px] text-muted'

export default function AdminPostForm() {
  const isAdmin = useIsAdmin()
  const { type: typeSlug, id } = useParams()
  const postType = getPostType(typeSlug)
  const navigate = useNavigate()
  const isNew = !id
  const { authors } = useAdminAuthors()
  const [form, setForm] = useState({
    title: '',
    dek: '',
    date: '',
    image: '',
    imageAlt: '',
    imageCredit: '',
    metaDescription: '',
    author: DEFAULT_AUTHOR,
    bodyHtml: '',
    ingredientsText: '',
    status: 'published' as PostStatus,
    publishAt: '',
  })
  const [loadedCategory, setLoadedCategory] = useState<string | null>(null)
  // An admin's choice of post type, kept only for the post it was made on.
  const [typeChoice, setTypeChoice] = useState<{ route: string; category: string } | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(isNew)

  useEffect(() => {
    if (!isNew || authors.length === 0) return
    const sky = authors.find((person) => person.slug === 'sky' || person.name === DEFAULT_AUTHOR)
    if (!sky) return
    setForm((prev) => (prev.author === DEFAULT_AUTHOR || !authors.some((person) => person.name === prev.author) ? { ...prev, author: sky.name } : prev))
  }, [authors, isNew])

  useEffect(() => {
    if (isNew || !id) return
    api<AdminPost>(`/api/admin/posts/${id}`)
      .then((post) => {
        setLoadedCategory(post.category)
        setForm({
          title: post.title,
          dek: post.dek,
          date: post.date,
          image: post.image || '',
          imageAlt: post.imageAlt || '',
          imageCredit: post.imageCredit || '',
          metaDescription: post.metaDescription || '',
          author: post.author,
          bodyHtml: post.bodyHtml || '',
          ingredientsText: (post.ingredients || []).join('\n'),
          status: post.status === 'scheduled' || post.status === 'draft' ? post.status : 'published',
          publishAt: post.publishAt ? toDatetimeLocal(post.publishAt) : post.status === 'scheduled' ? defaultScheduleLocal() : '',
        })
        setReady(true)
      })
      .catch((err: Error) => setError(err.message))
  }, [id, isNew])

  function patch<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function onFeatured(file: File) {
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Images must be 5MB or smaller.')
      return
    }
    const uploaded = await apiUpload(file, 'post')
    patch('image', uploaded.key)
  }

  if (!postType) return <Navigate to="/admin" replace />
  const activeType = postType

  if (loadedCategory && loadedCategory !== activeType.category) {
    return <Navigate to={`/admin/${postTypeForCategory(loadedCategory).slug}/${id}`} replace />
  }

  const route = `${activeType.slug}/${id ?? 'new'}`
  const category = typeChoice?.route === route ? typeChoice.category : activeType.category
  const chosenType = postTypeForCategory(category)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    const payload = {
      title: form.title,
      dek: form.dek,
      category,
      date: form.date,
      image: form.image,
      imageAlt: form.imageAlt,
      imageCredit: form.imageCredit,
      metaDescription: form.metaDescription,
      author: form.author,
      bodyHtml: form.bodyHtml,
      ingredients: form.ingredientsText,
      status: form.status,
      publishAt: form.status === 'scheduled' && form.publishAt ? new Date(form.publishAt).toISOString() : undefined,
    }
    try {
      if (isNew) {
        const created = await api<AdminPost>('/api/admin/posts', { method: 'POST', body: JSON.stringify(payload) })
        navigate(`/admin/${chosenType.slug}/${created.id}`, { replace: true })
      } else {
        await api(`/api/admin/posts/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
        if (category !== activeType.category) {
          // Moved to another post type: follow it to its new tab.
          setLoadedCategory(category)
          navigate(`/admin/${chosenType.slug}/${id}`, { replace: true })
        }
      }
      await refreshCmsFeed()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  async function onDelete() {
    if (!id || !window.confirm(`Delete this ${activeType.singular.toLowerCase()}?`)) return
    await api(`/api/admin/posts/${id}`, { method: 'DELETE' })
    await refreshCmsFeed()
    navigate(`/admin/${activeType.slug}`)
  }

  if (!ready && !error) return <p className="text-muted">Loading…</p>

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="font-serif text-[32px] leading-none font-medium">
            {isNew ? `New ${activeType.singular}` : `Edit ${activeType.singular}`}
          </h2>
          <Link to={`/admin/${activeType.slug}`} className="text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose">
            {activeType.allLabel}
          </Link>
        </div>
        {error ? <p className="mb-4 text-sm text-rose">{error}</p> : null}
        <label className="block">
          <span className={labelClass}>Title</span>
          <input required value={form.title} onChange={(event) => patch('title', event.target.value)} className={`${inputClass} font-serif text-[22px]`} />
        </label>
        <label className="mt-5 block">
          <span className={labelClass}>Excerpt</span>
          <textarea
            rows={3}
            value={form.dek}
            onChange={(event) => patch('dek', event.target.value)}
            className={inputClass}
            placeholder="Shown on archives and cards"
          />
        </label>
        <div className="mt-5">
          <span className={labelClass}>Content</span>
          <PostEditor value={form.bodyHtml} onChange={(html) => patch('bodyHtml', html)} />
        </div>
        {category === 'Food' ? (
          <label className="mt-5 block">
            <span className={labelClass}>Ingredients</span>
            <textarea
              rows={8}
              value={form.ingredientsText}
              onChange={(event) => patch('ingredientsText', event.target.value)}
              className={inputClass}
              placeholder={'One ingredient per line\n1 cup flour\n2 eggs'}
            />
          </label>
        ) : null}
      </div>

      <aside className="flex flex-col gap-5">
        {isAdmin ? (
          <label className="block">
            <span className={labelClass}>Post type</span>
            <select
              value={category}
              onChange={(event) => setTypeChoice({ route, category: event.target.value })}
              className={inputClass}
            >
              {POST_TYPES.map((type) => (
                <option key={type.slug} value={type.category}>
                  {type.menu}
                </option>
              ))}
            </select>
            {category !== activeType.category ? (
              <span className="mt-1.5 block text-[12px] text-rose">
                {isNew ? `Will be saved under ${chosenType.menu}.` : `Moves to ${chosenType.menu} when you save.`}
              </span>
            ) : null}
          </label>
        ) : (
          <div>
            <span className={labelClass}>Post type</span>
            <p className="border border-rule bg-sand px-3 py-2.5 text-[15px]">{activeType.menu}</p>
          </div>
        )}
        <label className="block">
          <span className={labelClass}>Author</span>
          <select value={form.author} onChange={(event) => patch('author', event.target.value)} className={inputClass}>
            {!authors.some((person) => person.name === form.author) ? <option value={form.author}>{form.author}</option> : null}
            {authors.map((person) => (
              <option key={person.id} value={person.name}>
                {person.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={labelClass}>Status</span>
          <select
            value={form.status}
            onChange={(event) => {
              const status = event.target.value as PostStatus
              setForm((prev) => ({
                ...prev,
                status,
                publishAt: status === 'scheduled' && !prev.publishAt ? defaultScheduleLocal() : prev.publishAt,
              }))
            }}
            className={inputClass}
          >
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
            <option value="draft">Draft</option>
          </select>
        </label>
        {form.status === 'scheduled' ? (
          <label className="block">
            <span className={labelClass}>Publish on</span>
            <input
              type="datetime-local"
              required
              value={form.publishAt}
              onChange={(event) => patch('publishAt', event.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-[12px] text-muted">Stays hidden until this time, then goes live automatically.</p>
          </label>
        ) : (
          <label className="block">
            <span className={labelClass}>Date</span>
            <input
              type="date"
              value={storyDateToIso(form.date)}
              onChange={(event) => patch('date', isoToStoryDate(event.target.value))}
              className={inputClass}
            />
            <p className="mt-1 text-[12px] text-muted">
              {form.date && !storyDateToIso(form.date) ? `Currently “${form.date}”. ` : ''}
              {isNew ? 'Leave blank for today.' : 'Click to pick from a calendar.'}
            </p>
          </label>
        )}
        <div>
          <span className={labelClass}>Featured image</span>
          {form.image ? (
            <img src={mediaUrl(form.image, 'card')} alt={form.imageAlt || ''} className="mb-3 aspect-[4/5] w-full object-cover object-top" />
          ) : null}
          <FilePicker label={form.image ? 'Replace image' : 'Choose file'} onFile={(file) => void onFeatured(file)} />
          <p className="mt-2 text-[12px] text-muted">5MB max. Stored in the media bucket as WebP.</p>
        </div>
        <label className="block">
          <span className={labelClass}>Featured image alt text</span>
          <input value={form.imageAlt} onChange={(event) => patch('imageAlt', event.target.value)} className={inputClass} />
        </label>
        <label className="block">
          <span className={labelClass}>Image credit</span>
          <input value={form.imageCredit} onChange={(event) => patch('imageCredit', event.target.value)} className={inputClass} />
        </label>
        <label className="block">
          <span className={labelClass}>Meta description</span>
          <textarea
            rows={4}
            maxLength={320}
            value={form.metaDescription}
            onChange={(event) => patch('metaDescription', event.target.value)}
            className={inputClass}
          />
        </label>
        <button type="submit" disabled={busy} className="text-caps h-12 bg-ink text-white hover:bg-rose disabled:opacity-60">
          {busy ? 'Saving' : form.status === 'scheduled' ? `Schedule ${activeType.singular.toLowerCase()}` : `Save ${activeType.singular.toLowerCase()}`}
        </button>
        {!isNew && isAdmin ? (
          <button type="button" onClick={() => void onDelete()} className="text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose">
            Delete
          </button>
        ) : null}
      </aside>
    </form>
  )
}

// Posts store their display date as "MM / DD / YY"; the date input works in "YYYY-MM-DD".
function storyDateToIso(value: string) {
  const match = /^(\d{1,2}) \/ (\d{1,2}) \/ (\d{2})$/.exec(value.trim())
  if (!match) return ''
  const [, month, day, year] = match
  return `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

function isoToStoryDate(value: string) {
  const match = /^\d{2}(\d{2})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return ''
  const [, year, month, day] = match
  return `${month} / ${day} / ${year}`
}
